import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export interface PickupRateBracket {
  weightFromKg: number;
  weightToKg: number;
}

export interface PickupRateCell {
  base: string;
  perKg: string;
}

export interface PickupRateAreaRow {
  areaId: string;
  name: string;
  cells: Record<string, PickupRateCell>;
}

export const pickupRateBracketKey = (bracket: PickupRateBracket) =>
  `${bracket.weightFromKg}:${bracket.weightToKg}`;

export function PickupRateLanes({
  brackets,
  rows,
  isLoading,
  onUpdateCell,
  onRemoveRow,
  onRemoveBracket,
}: {
  brackets: PickupRateBracket[];
  rows: PickupRateAreaRow[];
  isLoading: boolean;
  onUpdateCell: (
    areaId: string,
    bracket: PickupRateBracket,
    field: keyof PickupRateCell,
    value: string
  ) => void;
  onRemoveRow: (areaId: string) => void;
  onRemoveBracket: (bracket: PickupRateBracket) => void;
}) {
  return (
    <>
      <div className="space-y-3 md:hidden">
        {brackets.length > 0 && (
          <fieldset className="flex flex-wrap gap-2">
            <legend className="sr-only">Configured weight brackets</legend>
            {brackets.map((bracket) => (
              <div
                key={pickupRateBracketKey(bracket)}
                className="inline-flex min-h-11 items-center gap-1 rounded-md border bg-muted/30 pl-3"
              >
                <span className="text-xs font-medium">
                  {bracket.weightFromKg}–{bracket.weightToKg} kg
                </span>
                <Button
                  variant="ghost"
                  size="icon-touch"
                  aria-label={`Remove ${bracket.weightFromKg}–${bracket.weightToKg} kg bracket mobile`}
                  onClick={() => onRemoveBracket(bracket)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </fieldset>
        )}
        {isLoading ? (
          <div role="status" className="p-6 text-center text-sm">
            Loading rates…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No sender lanes configured. Add a sender service area below.
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.areaId} className="space-y-3 rounded-md border p-3">
              <div className="flex min-h-11 items-center justify-between gap-2">
                <h3 className="text-sm font-medium">{row.name}</h3>
                <Button
                  variant="ghost"
                  size="icon-touch"
                  aria-label={`Remove ${row.name}`}
                  onClick={() => onRemoveRow(row.areaId)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {brackets.map((bracket) => {
                const cell = row.cells[pickupRateBracketKey(bracket)] ?? {
                  base: "",
                  perKg: "",
                };
                const cellId = `pickup-rate-mobile-${row.areaId}-${pickupRateBracketKey(bracket)}`;
                return (
                  <fieldset
                    key={pickupRateBracketKey(bracket)}
                    className="grid grid-cols-2 gap-2 rounded-md bg-muted/40 p-3"
                  >
                    <legend className="px-1 text-xs font-medium">
                      {bracket.weightFromKg}–{bracket.weightToKg} kg
                    </legend>
                    <RateInput
                      id={`${cellId}-base`}
                      label="Base $"
                      ariaLabel={`${row.name} ${bracket.weightFromKg}–${bracket.weightToKg} kg base`}
                      value={cell.base}
                      onChange={(value) =>
                        onUpdateCell(row.areaId, bracket, "base", value)
                      }
                    />
                    <RateInput
                      id={`${cellId}-per-kg`}
                      label="Per kg $"
                      ariaLabel={`${row.name} ${bracket.weightFromKg}–${bracket.weightToKg} kg per kg`}
                      value={cell.perKg}
                      onChange={(value) =>
                        onUpdateCell(row.areaId, bracket, "perKg", value)
                      }
                    />
                  </fieldset>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-48">Sender service area</TableHead>
              {brackets.map((bracket) => (
                <TableHead
                  key={pickupRateBracketKey(bracket)}
                  className="min-w-56"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      {bracket.weightFromKg}–{bracket.weightToKg} kg
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Remove ${bracket.weightFromKg}–${bracket.weightToKg} kg bracket`}
                      onClick={() => onRemoveBracket(bracket)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableHead>
              ))}
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={brackets.length + 2}
                  className="py-10 text-center text-muted-foreground"
                >
                  Loading rates…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={brackets.length + 2}
                  className="py-10 text-center text-muted-foreground"
                >
                  No sender lanes configured. Add a sender service area below.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.areaId}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  {brackets.map((bracket) => {
                    const cell = row.cells[pickupRateBracketKey(bracket)] ?? {
                      base: "",
                      perKg: "",
                    };
                    const cellId = `pickup-rate-${row.areaId}-${pickupRateBracketKey(bracket)}`;
                    return (
                      <TableCell key={pickupRateBracketKey(bracket)}>
                        <div className="grid grid-cols-2 gap-2">
                          <RateInput
                            id={`${cellId}-base`}
                            label="Base $"
                            ariaLabel={`${row.name} ${bracket.weightFromKg}–${bracket.weightToKg} kg base desktop`}
                            value={cell.base}
                            onChange={(value) =>
                              onUpdateCell(row.areaId, bracket, "base", value)
                            }
                          />
                          <RateInput
                            id={`${cellId}-per-kg`}
                            label="Per kg $"
                            ariaLabel={`${row.name} ${bracket.weightFromKg}–${bracket.weightToKg} kg per kg desktop`}
                            value={cell.perKg}
                            onChange={(value) =>
                              onUpdateCell(row.areaId, bracket, "perKg", value)
                            }
                          />
                        </div>
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${row.name}`}
                      onClick={() => onRemoveRow(row.areaId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function RateInput({
  id,
  label,
  ariaLabel,
  value,
  onChange,
}: {
  id: string;
  label: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label htmlFor={id} className="space-y-1 text-xs text-muted-foreground">
      <span>{label}</span>
      <Input
        id={id}
        aria-label={ariaLabel}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="—"
      />
    </label>
  );
}
